(() => {
  const DATA_SELECTOR = '[data-camo-variant-galleries]';
  const STACK_SELECTOR = '[data-camo-product-flavor-stack-slide], [data-camo-product-flavor-stack-thumbnail]';
  const EVENT_NAMES = ['variant:change', 'product:variant-change', 'theme:variant:change', 'xo:variant:change'];
  let refreshId = 0;

  const parseJson = (element, fallback = null, label = 'JSON') => {
    if (!element) return fallback;
    const source = element.textContent || '';
    try {
      return JSON.parse(source);
    } catch (strictError) {
      // XO's existing product-data snippet includes a trailing comma. Normalize only
      // trailing object/array commas, then retry without changing valid JSON values.
      try {
        return JSON.parse(source.replace(/,\s*([}\]])/g, '$1'));
      } catch (normalizedError) {
        console.warn(`Variant galleries: invalid ${label}.`, normalizedError);
        return fallback;
      }
    }
  };

  const normalize = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

  const refreshCarousel = (carousel) => {
    if (!carousel) return;
    refreshId += 1;
    const observedValue = `camo-variant-gallery-${refreshId}`;
    carousel.setAttribute('xo-observed', observedValue);
    carousel.querySelectorAll('xo-carousel-list, xo-carousel-thumbnail').forEach((element) => {
      element.setAttribute('xo-observed', observedValue);
    });
  };

  const navigateToStack = (mediaRoot) => {
    mediaRoot.dataset.camoFlavorStackActive = 'true';
    mediaRoot.querySelectorAll('xo-carousel').forEach((carousel) => {
      const list = primaryLists(carousel)[0];
      const slide = list?.querySelector('[data-camo-product-flavor-stack-slide]');
      if (!list || !slide) return;

      const slides = Array.from(list.querySelectorAll(':scope > xo-carousel-slide:not([xo-cloned])'));
      const index = slides.indexOf(slide);
      if (index < 0) return;

      if (window.xoCarousel?.goTo && carousel.xoName) {
        window.xoCarousel.goTo(carousel.xoName, index);
        return;
      }

      carousel.querySelector('[data-camo-product-flavor-stack-thumbnail]')?.click();
    });
  };

  const directMediaChildren = (container) => Array.from(container.children).filter((node) => {
    return node.matches?.('[data-media-id]') && !node.matches(STACK_SELECTOR) && !node.hasAttribute('xo-cloned');
  });

  const primaryLists = (root) => Array.from(root.querySelectorAll('xo-carousel-list')).filter((list) => {
    return !list.closest('xo-carousel-thumbnail');
  });

  const stackIsActive = (mediaRoot) => {
    const primarySlides = primaryLists(mediaRoot).flatMap((list) => (
      Array.from(list.querySelectorAll(':scope > xo-carousel-slide:not([xo-cloned])'))
    ));
    const activePrimary = primarySlides.find((slide) => (
      slide.hasAttribute('xo-active') && slide.getAttribute('xo-active') !== 'false'
    ));
    if (activePrimary) return activePrimary.hasAttribute('data-camo-product-flavor-stack-slide');
    return mediaRoot.dataset.camoFlavorStackActive === 'true';
  };

  const gridContainers = (root) => Array.from(root.querySelectorAll('xo-gallery > div')).filter((container) => {
    return directMediaChildren(container).length > 0;
  });

  const selectedVariantId = (productRoot) => {
    const checked = productRoot.querySelector('input[name="id"]:checked');
    const field = productRoot.querySelector('select[name="id"], input[name="id"]');
    const urlId = new URLSearchParams(window.location.search).get('variant');
    return String(checked?.value || field?.value || urlId || '');
  };

  const selectedOptionMap = (productRoot, productData) => {
    const variants = Array.isArray(productData?.variants) ? productData.variants : [];
    const optionNames = Array.isArray(productData?.options) ? productData.options : [];
    const variantId = selectedVariantId(productRoot);
    const variant = variants.find((item) => String(item.id) === variantId) || variants.find((item) => item.available) || variants[0];
    const map = {};

    optionNames.forEach((name, index) => {
      const escapedName = window.CSS?.escape ? CSS.escape(name) : name.replace(/([\"'\[\]#.])/g, '\\$1');
      const checkedInput = productRoot.querySelector(`input[name="${escapedName}"]:checked`);
      const selectInput = productRoot.querySelector(`select[name="${escapedName}"]`);
      const variantValue = variant
        ? (Array.isArray(variant.options) ? variant.options[index] : variant[`option${index + 1}`])
        : '';
      const value = checkedInput?.value || selectInput?.value || variantValue;
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        map[normalize(name)] = normalize(value);
      }
    });
    return map;
  };

  const matchedMediaIds = (config, optionMap) => {
    if (!config || !Array.isArray(config.galleries)) return null;
    const activeKeys = Object.keys(optionMap);

    const match = config.galleries.find((gallery) => {
      if (!gallery || typeof gallery.options !== 'object' || Array.isArray(gallery.options)) return false;
      const keys = Object.keys(gallery.options);
      if (keys.length !== activeKeys.length) return false;
      return keys.every((name) => optionMap[normalize(name)] === normalize(gallery.options[name]));
    });

    if (!match || !Array.isArray(match.media_ids)) return null;
    const seen = new Set();
    return match.media_ids.map(String).filter((id) => id && !seen.has(id) && seen.add(id));
  };

  const makeRegistry = (container) => {
    const nativeNodes = directMediaChildren(container);
    return {
      container,
      nativeNodes,
      byId: new Map(nativeNodes.map((node) => [String(node.dataset.mediaId), node])),
    };
  };

  const attachInOrder = (registry, orderedIds) => {
    const { container, nativeNodes, byId } = registry;
    const stackNodes = Array.from(container.children).filter((node) => node.matches?.(STACK_SELECTOR));
    nativeNodes.forEach((node) => node.remove());

    const orderedNodes = orderedIds === null
      ? nativeNodes
      : orderedIds.map((id) => byId.get(String(id))).filter(Boolean);

    const fragment = document.createDocumentFragment();
    orderedNodes.forEach((node) => fragment.appendChild(node));
    stackNodes.forEach((node) => fragment.appendChild(node));
    container.appendChild(fragment);
  };

  const pairThumbnailIds = (root, primaryRegistries) => {
    root.querySelectorAll('xo-carousel').forEach((carousel) => {
      const primary = primaryRegistries.find((registry) => registry.container.closest('xo-carousel') === carousel);
      const thumbList = carousel.querySelector('xo-carousel-thumbnail xo-carousel-list');
      if (!primary || !thumbList) return;

      const thumbs = Array.from(thumbList.children).filter((node) => {
        return node.matches?.('xo-carousel-slide') && !node.matches(STACK_SELECTOR) && !node.hasAttribute('xo-cloned');
      });
      thumbs.forEach((thumb, index) => {
        const mediaId = primary.nativeNodes[index]?.dataset.mediaId;
        if (mediaId && !thumb.dataset.mediaId) thumb.dataset.mediaId = mediaId;
      });
    });
  };

  const initProduct = (productRoot) => {
    if (!productRoot || productRoot.dataset.camoVariantGalleriesReady === 'true') return;
    const configElement = productRoot.querySelector(DATA_SELECTOR);
    if (!configElement) return;

    const config = parseJson(configElement, null, 'custom.variant_galleries JSON');
    const productData = parseJson(
      productRoot.querySelector('xo-product-data script[type="application/json"]'),
      null,
      'XO product data'
    );
    if (!config || !productData) return;

    productRoot.dataset.camoVariantGalleriesReady = 'true';
    const mediaRoot = productRoot.querySelector('.main-product__media-with-camo-stack') || productRoot;
    const primaryRegistries = primaryLists(mediaRoot).map(makeRegistry);
    pairThumbnailIds(mediaRoot, primaryRegistries);
    const thumbnailRegistries = Array.from(mediaRoot.querySelectorAll('xo-carousel-thumbnail xo-carousel-list')).map(makeRegistry);
    const gridRegistries = gridContainers(mediaRoot).map(makeRegistry);
    const registries = [...primaryRegistries, ...thumbnailRegistries, ...gridRegistries];
    let lastSignature = '';
    let applyTimer = 0;
    let navigateToStackAfterApply = false;
    let stackNavigationId = 0;

    const apply = () => {
      const optionMap = selectedOptionMap(productRoot, productData);
      const mediaIds = matchedMediaIds(config, optionMap);
      const signature = JSON.stringify({ optionMap, mediaIds });
      const shouldNavigateToStack = navigateToStackAfterApply || stackIsActive(mediaRoot);
      navigateToStackAfterApply = false;

      if (signature !== lastSignature) {
        registries.forEach((registry) => attachInOrder(registry, mediaIds));
        mediaRoot.querySelectorAll('xo-carousel').forEach(refreshCarousel);
        lastSignature = signature;
      }

      if (shouldNavigateToStack) {
        window.clearTimeout(stackNavigationId);
        stackNavigationId = window.setTimeout(() => {
          requestAnimationFrame(() => requestAnimationFrame(() => navigateToStack(mediaRoot)));
        }, 0);
      }
    };

    const scheduleApply = () => {
      window.clearTimeout(applyTimer);
      applyTimer = window.setTimeout(() => requestAnimationFrame(apply), 0);
    };

    productRoot.addEventListener('change', scheduleApply);
    productRoot.addEventListener('input', scheduleApply);
    EVENT_NAMES.forEach((eventName) => productRoot.addEventListener(eventName, scheduleApply));
    productRoot.addEventListener('camo:product-flavor-visual-state', (event) => {
      navigateToStackAfterApply = event.detail?.focusStack === true || stackIsActive(mediaRoot);
      scheduleApply();
    });

    mediaRoot.addEventListener('click', (event) => {
      const stackThumb = event.target.closest('[data-camo-product-flavor-stack-thumbnail]');
      const nativeThumb = event.target.closest('xo-carousel-thumbnail xo-carousel-slide:not([data-camo-product-flavor-stack-thumbnail])');
      if (stackThumb) mediaRoot.dataset.camoFlavorStackActive = 'true';
      else if (nativeThumb) mediaRoot.dataset.camoFlavorStackActive = 'false';
    }, true);

    const observer = new MutationObserver((mutations) => {
      let relevant = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const target = mutation.target;
          const inThumbnail = Boolean(target.closest?.('xo-carousel-thumbnail'));
          if (!inThumbnail && target.matches?.('[data-camo-product-flavor-stack-slide]')) {
            mediaRoot.dataset.camoFlavorStackActive = target.getAttribute('xo-active') !== 'false';
          } else if (!inThumbnail && target.matches?.('xo-carousel-slide:not([data-camo-product-flavor-stack-slide])') && target.getAttribute('xo-active') !== 'false') {
            mediaRoot.dataset.camoFlavorStackActive = 'false';
          }
          return;
        }
        if (Array.from(mutation.addedNodes).some((node) => node.nodeType === 1 && node.matches?.(STACK_SELECTOR))) relevant = true;
      });
      if (relevant) scheduleApply();
    });
    observer.observe(mediaRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['xo-active'] });

    apply();
  };

  const init = (scope = document) => {
    if (scope.matches?.('xo-product[xo-product-information]')) initProduct(scope);
    scope.querySelectorAll?.('xo-product[xo-product-information]').forEach(initProduct);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
